import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import { ButtonV2 } from '../../button-v2/button-v2';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import Dialog from '../../modal-dialog/dialog.component';

export default function Signature({
  label,
  value,
  error,
  readOnly,
  required,
  onChange,
  labelStyles,
}: DynamicFieldComponentProps<string>) {
  const [signatureValue, setSignatureValue] = useState<string>(value || '');
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const placeholder =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASAAAABgCAYAAAC9rK9YAAAAAXNSR0IArs4c6QAAAq5JREFUeF7t1MEJAAAIAzG7/9KOcZ+4QCHI7RwBAgQigUW7ZgkQIHAC5AkIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQICJAfIEAgExCgjN4wAQIC5AcIEMgEBCijN0yAgAD5AQIEMgEByugNEyAgQH6AAIFMQIAyesMECAiQHyBAIBMQoIzeMAECAuQHCBDIBAQoozdMgIAA+QECBDIBAcroDRMgIEB+gACBTECAMnrDBAgIkB8gQCATEKCM3jABAgLkBwgQyAQEKKM3TICAAPkBAgQyAQHK6A0TICBAfoAAgUxAgDJ6wwQIPJ9tAGFfzsTEAAAAAElFTkSuQmCC';

  const canvas = canvasRef.current;
  const ctx = canvasRef.current?.getContext('2d');
  const drawingCoordinates = { x: 0, y: 0 };
  const image = new Image();
  const drawImage = () => canvas?.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.src = signatureValue || '';
  image.onload = () => drawImage();

  const getEventCoordinates = (event: MouseEvent | TouchEvent) => {
    if (event instanceof MouseEvent) {
      return { clientX: event.clientX, clientY: event.clientY };
    } else if (event.touches && event.touches.length > 0) {
      return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
    }

    return {};
  };

  const setDrawingPosition = (event: MouseEvent | TouchEvent) => {
    if (canvas) {
      const canvasOffset = canvas.getBoundingClientRect();
      const { clientX, clientY } = getEventCoordinates(event);

      if (clientX && clientY) {
        drawingCoordinates.x = clientX - canvasOffset.left;
        drawingCoordinates.y = clientY - canvasOffset.top;
      }
    }
  };

  const enableDrawing = (event: MouseEvent | TouchEvent) => {
    canvas?.addEventListener('mousemove', drawing);
    canvas?.addEventListener('touchmove', drawing);
    setDrawingPosition(event);
  };

  const disableDrawing = () => {
    canvas?.removeEventListener('mousemove', drawing);
    canvas?.removeEventListener('touchmove', drawing);
  };

  const drawing = (event: MouseEvent | TouchEvent) => {
    ctx?.beginPath();
    ctx?.moveTo(drawingCoordinates.x, drawingCoordinates.y);
    setDrawingPosition(event);
    ctx?.lineTo(drawingCoordinates.x, drawingCoordinates.y);
    ctx?.stroke();
  };

  const clearCanvas = () => {
    if (canvas) {
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const saveCanvas = () => {
    const newSign = canvas?.toDataURL('image/png') || placeholder;
    const newValue = newSign === placeholder ? '' : newSign;
    setSignatureValue(newValue);
    onChange?.(newValue);
    setVisible(false);
  };

  const showDialog = () => setVisible(true);
  const onDialogHidden = () => {
    clearCanvas();
    drawImage();
    setVisible(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: canvas, enableDrawing, disableDrawing are derived from canvasRef.current which ctx also depends on
  useEffect(() => {
    if (ctx) {
      ctx.strokeStyle = '#333';
      ctx.lineCap = 'round';
      ctx.lineWidth = 3;
    }

    canvas?.addEventListener('mousedown', enableDrawing);
    document.addEventListener('mouseup', disableDrawing);
    canvas?.addEventListener('touchstart', enableDrawing);
    document.addEventListener('touchend', disableDrawing);

    return () => {
      canvas?.removeEventListener('mousedown', enableDrawing);
      window.removeEventListener('mouseup', disableDrawing);
      canvas?.removeEventListener('touchstart', enableDrawing);
      document.removeEventListener('touchend', disableDrawing);
    };
  }, [ctx]);

  useEffect(() => {
    setSignatureValue(value ?? '');
  }, [value]);

  const signatureFieldStyle = cn('mt-1 h-24 rounded-md border bg-background md:w-72', {
    'border-error': error,
    'border-input': !error,
    'opacity-70': readOnly,
  });

  const canvasContainerStyle = cn('mt-1 h-24 w-full max-w-64 overflow-hidden rounded-md border bg-background', {
    'border-error': error,
    'border-input': !error,
  });

  return (
    <div>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {!readOnly && (
        <ButtonV2 variant="link" onClick={showDialog} className="-mx-1 px-1">
          Click to Sign
        </ButtonV2>
      )}
      <div className={signatureFieldStyle}>
        {/* Signatures are rasterised dark (#333) ink; invert in dark mode so they stay
            visible on the dark background surface. The stored PNG keeps its original ink. */}
        {signatureValue && (
          <img src={signatureValue} alt="signature" className="block max-h-full max-w-full dark:invert" />
        )}
      </div>
      {error && <ErrorLabel>{error}</ErrorLabel>}

      <Dialog
        header=""
        visible={visible}
        onClose={onDialogHidden}
        contentClassName="px-1"
        buttons={[
          { name: 'clear', text: 'Clear', variant: 'outline', onClick: clearCanvas },
          { name: 'submit', text: 'Submit', onClick: saveCanvas },
        ]}
      >
        <div className={canvasContainerStyle}>
          <canvas width={256} height={96} ref={canvasRef} className="cursor-crosshair dark:invert"></canvas>
        </div>
      </Dialog>
    </div>
  );
}
